import { jupiterFetch } from '@svenskaspelab/nova-core/jupiter';

import type { DataTransferObject } from '../../../@types/DataTransferObject.js';
import { RequestPaymentError } from '../../../error/RequestPaymentError.js';
import type { Wager } from '../../../wager/Wager.js';
import { createPaymentDTO } from '../utils/createPaymentDTO.js';
import { type ErrorResponse, isErrorResponse, type WagerResponse } from '../utils/types.js';

/**
 * Posts a given `Wager` to ITS via the TP-Proxy API.
 *
 * @param wager - A `Wager` instance
 * @throws {RequestPaymentError} If the payment didn't succeed
 * @returns - The response from the payment requests
 */
export async function postWager(wager: Wager): Promise<DataTransferObject> {
  try {
    const response = await jupiterFetch(`/wager/10/wagers`, {
      method: 'POST',
      body: JSON.stringify(wager),
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
      credentials: 'include',
      cache: 'no-store',
    });

    const data = (await response.json()) as WagerResponse | ErrorResponse;

    if (!response.ok || isErrorResponse(data)) {
      let code = '';
      let message = 'Unknown error';
      if (isErrorResponse(data)) {
        code = data.error.code;
        message = data.error.message ?? 'Unknown error';
      }

      throw new RequestPaymentError({
        clientCode: 'PAYMENT_REQUEST_FAILURE',
        code: code,
        extraData: { message },
      });
    }

    return createPaymentDTO(wager, data);
  } catch (error) {
    if (error instanceof Error && 'code' in error && typeof error.code === 'string') {
      switch (error.name) {
        case 'TypeError':
          throw new RequestPaymentError({
            clientCode: 'PAYMENT_FETCH_FAILURE',
            code: error.code,
            extraData: { message: error.message },
          });
        default:
          throw error;
      }
    } else {
      throw error;
    }
  }
}
